import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { CollectorsAPI } from '@/lib/services/insights/collector.service';
import { CollectorsCard } from './collectors-card';
import { MatrixText } from '../matrix-text';

// InfiniteScroll
function useInfiniteScroll({
  queryKey,
  queryFn,
  initialData,
  enabled = true,
  filters = {},
  scrollContainer = window,
}: any) {
  const { ...otherFilters } = filters;
  const isLoadingMore = useRef(false);

  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = {
        page: pageParam,
        ...otherFilters,
      };
      const result = await queryFn(params);
      return result;
    },
    getNextPageParam: lastPage => {
      if (lastPage.totalPages > lastPage.page) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialData: initialData
      ? {
          pages: [initialData],
          pageParams: [1],
        }
      : undefined,
    enabled,
    gcTime: 30 * 60 * 1000, // 30 minutes
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!scrollContainer.scrollThrottleTimeout) {
        scrollContainer.scrollThrottleTimeout = setTimeout(async () => {
          const viewportHeight = scrollContainer.clientHeight;
          const scrollPosition = scrollContainer.scrollTop;
          const totalHeight = scrollContainer.scrollHeight;
          const distanceToBottom = totalHeight - (scrollPosition + viewportHeight);

          console.log('Scroll Debug:', {
            distanceToBottom,
            viewportHeight,
            scrollPosition,
            totalHeight,
            hasNextPage: query.hasNextPage,
            isFetchingNextPage: query.isFetchingNextPage,
            isLoadingMore: isLoadingMore.current,
          });

          if (
            distanceToBottom < 200 &&
            query.hasNextPage &&
            !query.isFetchingNextPage &&
            !isLoadingMore.current &&
            enabled
          ) {
            isLoadingMore.current = true;
            try {
              await query.fetchNextPage();
            } finally {
              isLoadingMore.current = false;
            }
          }
        }, 100);

        setTimeout(() => {
          delete scrollContainer.scrollThrottleTimeout;
        }, 150);
      }
    };
    handleScroll();

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollContainer.scrollThrottleTimeout) {
        clearTimeout(scrollContainer.scrollThrottleTimeout);
      }
    };
  }, [query.hasNextPage, query.isFetchingNextPage, enabled, query.fetchNextPage, query, scrollContainer, query.data]);

  return query;
}

export function CollectorsAll({ searchQuery, selectedTier }: { searchQuery: string; selectedTier: string }) {
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalDocs, setTotalDocs] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedTier, queryClient]);

  // Query parametrelerini hazırla
  const queryParams = useMemo(() => {
    return {
      query: debouncedSearch,
      tier: selectedTier !== 'All' ? parseInt(selectedTier) : undefined,
    };
  }, [debouncedSearch, selectedTier]);

  useEffect(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      scrollContainerRef.current = mainContent;
      setIsReady(true);
    }
  }, []);

  const { data, isLoading, isFetchingNextPage, hasNextPage, error, isFetching } = useInfiniteScroll({
    scrollContainer: scrollContainerRef.current,
    queryKey: ['collectors', queryParams], // 'leaderboard' yerine 'collectors' kullanıyoruz
    queryFn: async ({ page = 1 }) => {
      // sleep 200 ms
      await new Promise(resolve => setTimeout(resolve, 200));
      const result = await CollectorsAPI.getAllCollectors({
        page,
        query: queryParams.query,
        tier: queryParams.tier,
      });
      setTotalDocs(result.totalDocs);
      return result;
    },
    enabled: isReady,
  });

  // Connect all collectors
  const collectors = useMemo(() => {
    return data?.pages?.flatMap(page => page.docs) || [];
  }, [data]);

  // Update totalDocs when data changes
  useEffect(() => {
    if (data?.pages?.[0]?.totalDocs) {
      setTotalDocs(data.pages[0].totalDocs);
    }
  }, [data]);

  // Loading state
  if (isLoading && !collectors.length) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-zinc-500">Collectors Loading...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-zinc-500">Error loading collectors: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {collectors.map(collector => (
        <CollectorsCard
          key={collector.id}
          collector={collector}
          rank={collector.rank}
          showRank={true}
          showDetails={false}
          removeBorders={false}
          hideIds={false}
          expandable={false}
        />
      ))}

      {isFetching && hasNextPage && (
        <div className="text-center py-8">
          <div className="text-xs text-zinc-500">
            <MatrixText
              phrases={['Collectors Loading...']}
              className="tracking-wider"
              interval={1500}
              glitchProbability={0.05}
            />
          </div>
        </div>
      )}

      {isFetching && !hasNextPage && (
        <div className="text-center py-8">
          <p className="text-xs text-zinc-500">All Collectors Loaded</p>
        </div>
      )}

      {error && (
        <div className="text-center py-8">
          <p className="text-xs text-zinc-500">Error loading collectors: {(error as Error).message}</p>
        </div>
      )}
    </div>
  );
}
